import { type ComponentType, type ReactNode, type SVGProps } from 'react';
import { CloseIcon, WarnIcon } from '@/icons';
import styles from '@/components/ConfirmModal/ConfirmModal.module.css';

interface ConfirmModalProps {
  /** Top header text. */
  readonly title: string;
  /** Badge icon component; defaults to a warning triangle. */
  readonly icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** Large centered line (e.g. the subject being acted on). Hidden if omitted. */
  readonly headline?: string;
  /** Explanatory warning row. Hidden if omitted. */
  readonly message?: ReactNode;
  /** Confirm button label. */
  readonly confirmLabel: string;
  /** Cancel button label. Omit to hide the cancel button entirely. */
  readonly cancelLabel?: string;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

/**
 * Generic confirmation dialog. All copy comes from props so it can back any
 * confirm/cancel flow (re-home, delete, stop a program, …). Clicking the
 * backdrop or the ✕ calls onClose; the cancel button is only rendered when
 * `cancelLabel` is provided.
 */
export function ConfirmModal({
  title,
  icon: Icon = WarnIcon,
  headline,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
}: ConfirmModalProps): JSX.Element {
  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
          <button className={styles.close} onClick={onClose} type="button" aria-label="Close">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <div className={styles.badge}>
          <span className={styles.badgeGlow} />
          <Icon className={styles.badgeIcon} />
        </div>

        {headline !== undefined && (
          <div className={styles.headline}>
            <span className={styles.line1}>{headline}</span>
          </div>
        )}

        {message !== undefined && (
          <div className={styles.warning}>
            <WarnIcon width={20} height={20} />
            {message}
          </div>
        )}

        <div className={styles.footer}>
          {cancelLabel !== undefined && (
            <button type="button" className={styles.btnGhost} onClick={onClose}>
              {cancelLabel}
            </button>
          )}
          <button type="button" className={styles.btnConfirm} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
